import React, { useState } from 'react';
import { Card, Alert, Badge, Button, Spinner, ListGroup } from 'react-bootstrap';
import { CheckCircle, XCircle, ExclamationTriangle, ArrowClockwise, InfoCircle } from 'react-bootstrap-icons';
import { useCheckBounceComplaintSetupQuery, useSetupBounceComplaintHandlingMutation } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useAccount } from '../contexts/AccountContext';

const BounceComplaintSetupStatus = () => {
  const [showDetails, setShowDetails] = useState(false);
  const { currentUser } = useUser();
  const { selectedAccount } = useAccount();
  const userEmail = currentUser?.email || '';
  
  // Get accountId and region from selected account
  const accountId = selectedAccount?.id && selectedAccount.id !== 'all' ? selectedAccount.id : null;
  const region = selectedAccount?.region || 'us-east-1'; // Default to us-east-1 if not specified
  
  const { 
    data: setupCheckData, 
    isLoading: isChecking, 
    error: checkError,
    refetch: checkSetup 
  } = useCheckBounceComplaintSetupQuery(
    { 
      userEmail: userEmail || null,
      accountId: accountId,
      region: region
    },
    {
      skip: !userEmail, // Skip query if no user email
      refetchOnMountOrArgChange: true
    }
  );

  const [setupBounceComplaintHandling, { isLoading: isSettingUp }] = useSetupBounceComplaintHandlingMutation();

  const checks = setupCheckData?.data?.checks || [];
  const summary = setupCheckData?.data?.summary || {};
  const allPassed = setupCheckData?.data?.allPassed || false;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="text-success me-2" size={16} />;
      case 'error':
        return <XCircle className="text-danger me-2" size={16} />;
      case 'warning':
        return <ExclamationTriangle className="text-warning me-2" size={16} />;
      default:
        return <InfoCircle className="text-info me-2" size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge bg="success">Passed</Badge>;
      case 'error':
        return <Badge bg="danger">Failed</Badge>;
      case 'warning':
        return <Badge bg="warning">Warning</Badge>;
      default:
        return <Badge bg="secondary">Checking...</Badge>;
    }
  };

  const handleSetupNow = async () => {
    if (!userEmail) {
      alert('Please ensure your email address is set in your profile before setting up bounce/complaint handling.');
      return;
    }

    try {
      await setupBounceComplaintHandling({
        emailAddress: userEmail,
        accountId: accountId,
        region: region
      }).unwrap();
      // Refresh check after setup
      setTimeout(() => {
        checkSetup();
      }, 2000);
    } catch (error) {
      console.error('Failed to setup bounce/complaint handling:', error);
      alert(`Failed to setup bounce/complaint handling: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">Bounce/Complaint Handling Setup Status</h6>
          <small className="text-muted">
            Required for SES production access
          </small>
        </div>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => checkSetup()}
            disabled={isChecking}
            className="me-2"
          >
            {isChecking ? (
              <>
                <Spinner size="sm" className="me-1" animation="border" />
                Checking...
              </>
            ) : (
              <>
                <ArrowClockwise size={14} className="me-1" />
                Recheck
              </>
            )}
          </Button>
          {!allPassed && userEmail && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleSetupNow}
              disabled={isSettingUp}
            >
              {isSettingUp ? (
                <>
                  <Spinner size="sm" className="me-1" animation="border" />
                  Setting up...
                </>
              ) : (
                'Setup Now'
              )}
            </Button>
          )}
          {!userEmail && (
            <Alert variant="warning" className="mb-0 mt-2">
              <InfoCircle className="me-2" />
              <small>Please set your email address in your profile to enable setup.</small>
            </Alert>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        {isChecking ? (
          <div className="text-center py-3">
            <Spinner animation="border" className="me-2" />
            <span>Checking setup status...</span>
          </div>
        ) : checkError ? (
          <Alert variant="danger">
            <XCircle className="me-2" />
            Failed to check setup status. Please try again.
          </Alert>
        ) : !userEmail ? (
          <Alert variant="warning">
            <ExclamationTriangle className="me-2" />
            <strong>Email Address Required:</strong> Please ensure your email address is set in your profile before setting up bounce/complaint handling.
          </Alert>
        ) : checks.length === 0 ? (
          <Alert variant="info">
            <InfoCircle className="me-2" />
            No setup checks available. Run setup first.
          </Alert>
        ) : (
          <>
            {/* Summary */}
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <strong>Overall Status:</strong>
                {allPassed ? (
                  <Badge bg="success" className="fs-6">
                    <CheckCircle className="me-1" size={16} />
                    All Checks Passed
                  </Badge>
                ) : (
                  <Badge bg="warning" className="fs-6">
                    <ExclamationTriangle className="me-1" size={16} />
                    {summary.errors || 0} Issue(s) Found
                  </Badge>
                )}
              </div>
              <div className="small text-muted">
                {summary.passed || 0} passed, {summary.warnings || 0} warnings, {summary.errors || 0} errors
              </div>
            </div>

            {/* Detailed Checks */}
            <div className="mb-3">
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                className="p-0 text-decoration-none"
              >
                {showDetails ? 'Hide' : 'Show'} Detailed Checks
              </Button>
            </div>

            {showDetails && (
              <ListGroup variant="flush">
                {checks.map((check, index) => (
                  <ListGroup.Item key={index} className="px-0">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          {getStatusIcon(check.status)}
                          <strong>{check.step}</strong>
                          <span className="ms-2">{getStatusBadge(check.status)}</span>
                        </div>
                        <div className="text-muted small ms-4">{check.message}</div>
                        {check.details && (
                          <div className="mt-2 ms-4">
                            <details className="small">
                              <summary className="text-muted cursor-pointer">View Details</summary>
                              <pre className="mt-2 p-2 bg-light rounded small">
                                {JSON.stringify(check.details, null, 2)}
                              </pre>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}

            {/* Action Required */}
            {!allPassed && (
              <Alert variant="warning" className="mt-3">
                <ExclamationTriangle className="me-2" />
                <strong>Action Required:</strong> Bounce/complaint handling is required for SES production access. 
                Click "Setup Now" to automatically configure, or review the detailed checks above.
                {checks.some(c => c.step.includes('Subscription') && c.status === 'warning') && userEmail && (
                  <div className="mt-2">
                    <strong>Note:</strong> If subscriptions are pending, check your email ({userEmail}) 
                    and confirm the SNS subscription emails.
                  </div>
                )}
              </Alert>
            )}

            {/* Success Message */}
            {allPassed && (
              <Alert variant="success" className="mt-3">
                <CheckCircle className="me-2" />
                <strong>Setup Complete!</strong> Bounce/complaint handling is fully configured. 
                You're ready to request SES production access.
              </Alert>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default BounceComplaintSetupStatus;

